import type { ReactNode } from 'react'
import type { ContentBlock } from '@shared/schemas/content'
import { SpeakButton } from '@web/lib/audio'

/**
 * Renders `*emphasis*` as italics.
 *
 * Not a markdown parser — deliberately just this one thing. Grammar
 * explanations lean on emphasis to carry the point ("the word agrees with the
 * person *speaking*"), and without it the asterisks show up as literal
 * characters in the middle of a sentence.
 */
function RichText({ text }: { text: string }): ReactNode {
  const parts = text.split(/\*([^*]+)\*/g)
  return parts.map((part, i) =>
    // Odd indices are the captured groups, i.e. the emphasised runs.
    i % 2 === 1 ? <em key={i}>{part}</em> : part,
  )
}

/** Renders one teaching block. Portuguese text is tagged `lang="pt-BR"` so
 *  screen readers and iOS voice pronounce it as Portuguese, not English. */
export function ContentBlockView({ block }: { block: ContentBlock }) {
  switch (block.type) {
    case 'text':
      return (
        <p className="text-lg leading-relaxed text-ink-900">
          <RichText text={block.text} />
        </p>
      )

    case 'tip':
      return (
        <aside className="rounded-xl border-l-4 border-gold-500 bg-cream px-4 py-3">
          <p className="text-sm font-semibold text-gold-700">Tip</p>
          <p className="mt-1 text-ink-900">
            <RichText text={block.text} />
          </p>
        </aside>
      )

    case 'culture':
      return (
        <aside className="rounded-xl border-l-4 border-[color:var(--color-sky-deep)] bg-sky-soft px-4 py-3">
          <p className="text-sm font-semibold text-ink-900">{block.title}</p>
          <p className="mt-1 text-ink-900">
            <RichText text={block.text} />
          </p>
        </aside>
      )

    case 'grammar':
      return (
        <section className="rounded-card bg-white p-5 ring-1 ring-brand-900/5">
          <h3 className="font-display text-lg font-bold text-ink-900">{block.title}</h3>
          <p className="mt-2 leading-relaxed text-ink-700">
            <RichText text={block.text} />
          </p>
          {block.examples.length > 0 && (
            <ul className="mt-4 space-y-2">
              {block.examples.map((example) => (
                <li key={example.pt} className="rounded-lg bg-sand-50 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-ink-900" lang="pt-BR">
                      {example.pt}
                    </p>
                    <SpeakButton text={example.pt} size="sm" />
                  </div>
                  <p className="text-sm text-ink-500">{example.en}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )

    case 'dialogue':
      return (
        <section className="space-y-3">
          {block.lines.map((line, index) => (
            <div key={index} className="rounded-card bg-white p-4 ring-1 ring-brand-900/5">
              <p className="text-xs font-semibold tracking-[0.16em] text-brand-500 uppercase">
                {line.speaker}
              </p>
              <div className="mt-1 flex items-center gap-2">
                <p className="flex-1 text-lg text-ink-900" lang="pt-BR">
                  {line.pt}
                </p>
                <SpeakButton text={line.pt} size="sm" />
              </div>
              <p className="mt-0.5 text-sm text-ink-500">{line.en}</p>
            </div>
          ))}
        </section>
      )

    case 'vocab_intro':
      return (
        <ul className="space-y-2">
          {block.items.map((item) => (
            <li
              key={item.pt}
              className="rounded-card bg-white p-4 ring-1 ring-brand-900/5"
            >
              <div className="flex items-center gap-2">
                <p className="flex-1 text-lg font-semibold text-ink-900" lang="pt-BR">
                  {item.pt}
                </p>
                <SpeakButton text={item.pt} />
              </div>
              <p className="text-ink-700">{item.en}</p>
              {item.note && <p className="mt-1 text-sm text-ink-500">{item.note}</p>}
            </li>
          ))}
        </ul>
      )

    case 'image':
      return (
        <figure>
          <div className="flex h-40 items-center justify-center rounded-card bg-sand-100 text-sm text-ink-400">
            {block.alt}
          </div>
          {block.caption && (
            <figcaption className="mt-2 text-sm text-ink-500">{block.caption}</figcaption>
          )}
        </figure>
      )

    case 'audio':
      return (
        <div className="rounded-card bg-sand-100 p-4">
          <p className="text-sm text-ink-500">
            {block.caption ?? 'Audio arrives with the audio system.'}
          </p>
          {block.transcript && (
            <p className="mt-2 text-ink-900" lang="pt-BR">
              {block.transcript}
            </p>
          )}
        </div>
      )
  }
}
